import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconUserCheck, IconUserOff, IconUserPlus } from '@tabler/icons-react';
import type { ManagerClientDetail, ManagerOption } from '@expeditor/shared';
import { managerClientsApi } from '../../shared/api/manager-clients';
import { useAuthStore } from '../../shared/auth/auth.store';

interface Props {
  client: ManagerClientDetail;
  onChange: (updated: ManagerClientDetail) => void;
}

/**
 * Блок «Ответственный менеджер» в карточке клиента.
 *
 * MANAGER видит только «Назначить меня» / «Освободить» (если он сам).
 * ADMIN видит дропдаун всех менеджеров + «Снять ответственного».
 */
export function ResponsibleManagerCard({ client, onChange }: Props) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isAdmin = role === 'ADMIN';
  const isManager = role === 'MANAGER';
  const me = user?.userId;

  const current = client.primaryManager;
  const isMine = current?.id === me;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin-передача
  const [modalOpen, setModalOpen] = useState(false);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!modalOpen || managers.length > 0) return;
    void managerClientsApi
      .listManagers()
      .then(setManagers)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'));
  }, [modalOpen, managers.length]);

  const set = async (managerId: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await managerClientsApi.setResponsibleManager(client.id, managerId);
      onChange(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сменить');
    } finally {
      setBusy(false);
    }
  };

  const submitTransfer = () => {
    if (!selected) return;
    void set(selected).then(() => {
      setModalOpen(false);
      setSelected(null);
    });
  };

  return (
    <Card withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={5}>Ответственный менеджер</Title>
          {current ? (
            <Text fw={500}>{current.fullName ?? current.email}</Text>
          ) : (
            <Badge color="gray" variant="light">
              не закреплён
            </Badge>
          )}
        </Group>

        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}

        <Group gap="xs">
          {/* MANAGER: «Назначить меня» если клиент не закреплён */}
          {isManager && !current && (
            <Button
              size="xs"
              leftSection={<IconUserPlus size={14} />}
              onClick={() => set(me!)}
              loading={busy}
            >
              Назначить меня
            </Button>
          )}
          {/* MANAGER: «Освободить» если клиент закреплён за мной */}
          {isManager && isMine && (
            <Button
              size="xs"
              variant="light"
              color="gray"
              leftSection={<IconUserOff size={14} />}
              onClick={() => set(null)}
              loading={busy}
            >
              Освободить
            </Button>
          )}

          {/* ADMIN: всегда «Передать» и «Снять» */}
          {isAdmin && (
            <>
              <Button
                size="xs"
                leftSection={<IconUserCheck size={14} />}
                onClick={() => setModalOpen(true)}
              >
                Передать менеджеру
              </Button>
              {current && (
                <Button
                  size="xs"
                  variant="light"
                  color="gray"
                  leftSection={<IconUserOff size={14} />}
                  onClick={() => set(null)}
                  loading={busy}
                >
                  Снять
                </Button>
              )}
            </>
          )}
        </Group>
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Передать клиента менеджеру"
        size="sm"
      >
        <Stack>
          <Select
            label="Менеджер"
            placeholder="Выберите"
            data={managers.map((m) => ({
              value: m.id,
              label: m.fullName ? `${m.fullName} (${m.email})` : m.email,
            }))}
            value={selected}
            onChange={setSelected}
            searchable
            nothingFoundMessage="Нет активных менеджеров"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Отмена
            </Button>
            <Button onClick={submitTransfer} disabled={!selected} loading={busy}>
              Передать
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
